import React, { useState, useEffect } from 'react';
import {
  withStyles,
  Divider,
  Grid,
  Button,
  CircularProgress,
  withTheme
} from '@material-ui/core';
import { compose } from 'recompose';
import Select from '../../../../../common/StyledSelect';
import { getDataWithQueryString, postData } from '../../../../../../api/componentAPIs';
import { withSnackbar } from 'notistack';
import MESSAGES from '../../../../../../appMessages';
import CustomTextField from '../../../../Dispatch/RouteExecution/DriverPopUp/common/CustomTextField';

const styles = (theme) => ({
  container: {
    minWidth: 320,
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    borderLeft: `1px solid ${theme.AppBGColor}`
  },
  content: {
    flexGrow: 1,
    overflow: 'auto',
    fontSize: theme.headerTextFontSize,
    color: theme.gridTextColor,
    padding: '8px 24px'
  },
  propertySection: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '4px',
    fontSize: theme.textFontSize
  },
  maxPSEValue: {
    fontWeight: 600
  },
  divider: {
    backgroundColor: theme.AppBGColor
  },
  selectRootClass: {
    marginTop: 0,
    marginBottom: 0
  },
  head: {
    fontSize: 14,
    color: theme.HeaderSelectedText,
    padding: '8px 24px',
    fontWeight: 600,
    flexBasis: 'auto'
  },
  footer: {
    padding: '24px',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '20px'
  },
  updateButton: {
    minWidth: '100px',
    '&$disabledButton': {
      backgroundColor: theme.iconColor
    }
  },
  disabledButton: {
    backgroundColor: theme.iconColor
  },
  circularProgress: {
    color: theme.white
  },
  label: {
    color: theme.TextColor,
    transform: 'translate(14px, 14px) scale(1)',
    fontSize: theme.headerTextFontSize,
    opacity: 0.7
  },
  inputRoot: {
    '& $notchedOutline,&:hover:not($inputFocused):not($inputError) $notchedOutline': {
      borderColor: theme.iconColor
    }
  },
  inputField: {
    padding: 12,
    fontSize: theme.textFontSize,
    color: theme.TextColor,
    height: 'initial'
  },
  gridContainer: {
    margin: '0px',
    padding: '10px',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
  },
   fullWidth: { 
    width: "100%",
    margin: 0
   },
});

const AddUpdateTrailer = ({
  classes,
  terminalId,
  isUpdateTrailer,
  selectedTrailerData,
  closeSideDrawer,
  updateTrailerDataInStore,
  enqueueSnackbar
}) => {
  const [availableTrailers, setAvailableTrailers] = useState([]);
  const [trailerData, setTrailerData] = useState({});
  const [errorInfo, setErrorInfo] = useState({});
  const [updateInProgress, setUpdateInProgress] = useState(false);

  useEffect(() => {
    if (isUpdateTrailer) {
      setSelectedTrailerData();
    } else {
      getTrailersToAdd();
    }
  }, []);

  const getTrailersToAdd = async () => {
    let response = await getDataWithQueryString('GetTrailersToAdd', `terminalId=${terminalId}`);
    if (response?.status >= 200 && response?.status < 400) {
      if (response.data?.length) {
        const trailerList = response.data.map((trailer) => getFormattedTrailerData(trailer));
        setAvailableTrailers(trailerList);
      } else {
        showSnackBar(MESSAGES.TRAILER_INVENTORY.NO_TRAILER_AVAILABLE_TO_ADD);
      }
    } else {
      showSnackBar(response.message);
    }
  };

  const setSelectedTrailerData = () => {
    if (selectedTrailerData) {
      let data = getFormattedTrailerData(selectedTrailerData);
      setTrailerData(data);
    }
  };

  const getFormattedTrailerData = (data) => {
    return {
      trailerName: data.trailerName,
      trailerId: data.trailerId,
      isLiftGateRequired: data.isLiftGateRequired,
      maxSkidsAllowed: data.maxSkidsAllowed,
      totalCount: data.totalCount ?? null
    };
  };

  const handleChange = (field) => (fieldVal) => {
    if (field == 'trailerId') {
      let value = fieldVal?.value;
      let currTrailer = availableTrailers.find((trailer) => trailer.trailerId === value);
      clearAllErrors();
      if (currTrailer) setTrailerData(currTrailer);
    } else if (field == 'totalCount') {
      showError('totalCount', null);
      setTrailerData((state) => ({ ...state, totalCount: Number(fieldVal) }));
    } else if (field == 'maxSkidsAllowed') {
      showError('maxSkidsAllowed', null);
      setTrailerData((state) => ({ ...state, maxSkidsAllowed: Number(fieldVal) }));
    }
  };

  const updateTrailerInventoryData = () => {
    if (!isTrailerDataValid()) {
      return;
    }
    if (isUpdateTrailer && isNoChange(trailerData, selectedTrailerData)) {
      closeSideDrawer();
      return;
    }
    const requestParams = {
      terminalId: terminalId,
      trailerList: [
        {
          trailerId: trailerData.trailerId,
          availableCount: trailerData.totalCount,
          maxSkid: trailerData.maxSkidsAllowed,
        }
      ]
    };
    setUpdateInProgress(true);
    postData('UpdateTrailers', requestParams).then(
      (response) => {
        if (Math.trunc(response.status / 100) === 2) {
          const successMessage = isUpdateTrailer
            ? MESSAGES.TRAILER_INVENTORY.TRAILER_TYPE_UPDATED
            : MESSAGES.TRAILER_INVENTORY.NEW_TRAILER_TYPE_ADDED;
          showSnackBar(successMessage, 'success');
          updateTrailerDataInStore();
          closeSideDrawer();
          return;
        }
        setUpdateInProgress(false);
      },
      () => {
        setUpdateInProgress(false);
      }
    );
  };

  const isTrailerDataValid = () => {
    if (!trailerData.trailerId) {
      showError('trailerId', MESSAGES.TRAILER_INVENTORY.TRAILER_TYPE_MISSING_ERROR);
      return false;
    }
    if (!trailerData.totalCount && trailerData.totalCount !== 0) {
      showError('totalCount', MESSAGES.TRAILER_INVENTORY.AVAILABLE_COUNT_MISSING_ERROR);
      return false;
    }
    if (trailerData.maxSkidsAllowed <= 0) {
      showError('maxSkidsAllowed', MESSAGES.TRAILER_INVENTORY.PSE_COUNT_GREATER_ZERO_ERROR);
      return false;
    }
    const isCountValid = isUpdateTrailer ? trailerData.totalCount >= 0 : trailerData.totalCount > 0;
    const countInvalidMessage = isUpdateTrailer
      ? MESSAGES.TRAILER_INVENTORY.AVAILABLE_COUNT_GREATER_THAN_OR_EQUAL_TO_ZERO_ERROR
      : MESSAGES.TRAILER_INVENTORY.AVAILABLE_COUNT_GREATER_ZERO_ERROR;
    if (!isCountValid) {
      if (!trailerData.totalCount) showError('totalCount', countInvalidMessage);
      return false;
    }
    return true;
  };

   const isNoChange = (updatedData, existingData) => {
    return updatedData.totalCount === existingData.totalCount && updatedData.maxSkidsAllowed === existingData.maxSkidsAllowed;
  };

  const showError = (field, message) => {
    setErrorInfo((state) => ({
      ...state,
      [field]: message
    }));
  };

  const clearAllErrors = () => {
    setErrorInfo({});
  };

  const showSnackBar = (message, variant = 'error') => {
    enqueueSnackbar(message, {
      variant: variant
    });
  };

  const title = isUpdateTrailer ? 'Update Trailer' : 'Add Trailer';
  const trailerTypeOptions = (isUpdateTrailer ? [selectedTrailerData] : availableTrailers).map(
    (item) => ({
      label: item.trailerName,
      value: item.trailerId
    })
  );

  return (
    <div className={`${classes.container} addUpdateTrailerForm`}>
      <div>
        <Grid item xs={12} className={classes.head}>
          {title}
        </Grid>
        <Divider className={classes.divider} />
      </div>
      <div className={classes.content}>
        <Grid container spacing={24} className={classes.gridContainer}>
          <div>
            <Select
              textFieldProps={{
                id: 'trailerId',
                label: 'Trailer Type',
                variant: 'outlined',
                helperText: errorInfo.trailerId
              }}
              rootClass={classes.selectRootClass}
              value={trailerData.trailerId}
              error={Boolean(errorInfo.trailerId)}
              onChange={handleChange('trailerId')}
              data={trailerTypeOptions}
              isDisabled={isUpdateTrailer}
              isNotClearable
            />
            <div className={classes.propertySection}>
              <div>{trailerData.isLiftGateRequired === 'L' && <i className="icon-liftgate" />}</div>
            </div>
          </div>
           <CustomTextField
            type="number"
            label="Max PSE"
            autoComplete="off"
            handleChange={handleChange('maxSkidsAllowed')}
            value={
              trailerData.maxSkidsAllowed ?? ''
            }
            error={errorInfo.maxSkidsAllowed}
            inputProps={{
              min: 0
            }}            
            mainClassProps={{ root: classes.fullWidth }}
          />
          <CustomTextField
            type="number"
            label="Available Count"
            autoComplete="off"
            handleChange={handleChange('totalCount')}
            value={
              trailerData.totalCount ?? ''
            }
            error={errorInfo.totalCount}
            inputProps={{
              min: 0
            }}           
            mainClassProps={{ root: classes.fullWidth }}
          />
        </Grid>
      </div>
      <div className={classes.footer}>
        <Button
          color="primary"
          variant="outlined"
          className={classes.cancelButton}
          onClick={closeSideDrawer}>
          Cancel
        </Button>
        <Button
          color="primary"
          variant="contained"
          classes={{
            root: classes.updateButton,
            disabled: classes.disabledButton
          }}
          onClick={updateTrailerInventoryData}>
          {updateInProgress ? (
            <CircularProgress size={16} classes={{ root: classes.circularProgress }} />
          ) : (
            title
          )}
        </Button>
      </div>
    </div>
  );
};

export default compose(withStyles(styles), withTheme(), withSnackbar)(AddUpdateTrailer);
